import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IconWrapper } from '../custom/icon';
import { cva } from 'class-variance-authority';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-[1002] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  'fixed left-[50%] top-[50%] z-[1002] translate-x-[-50%] translate-y-[-50%] w-full max-w-[90vw] max-h-[90vh] overflow-y-auto gap-3 border bg-background p-6 shadow-lg data-[state=open]:[animation:dialog-fade-in_150ms_ease-out] data-[state=closed]:[animation:dialog-fade-out_150ms_ease-in] sm:rounded-lg',
  {
    variants: {
      size: {
        default: 'w-[564px]',
        sm: 'w-[464px]',
        md: 'w-[810px]',
        lg: 'w-[900px]',
        xl: 'w-[1100px]',
        full: 'w-full w-full'
      },
      layout: {
        grid: 'grid',
        flex: 'flex flex-col'
      }
    },
    defaultVariants: {
      size: 'default',
      layout: 'grid'
    }
  }
);

const DialogContent = React.forwardRef(({ className, children, size, layout, hideCloseButton, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(dialogContentVariants({ size, layout, className }))}
      {...props}
    >
      {children}

      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-2 top-2 rounded-sm opacity-80 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <IconWrapper size="sm">
            <X />
          </IconWrapper>
          <span className="sr-only">Fechar</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn('flex flex-col gap-1.5', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const dialogTitleVariants = cva('text-lg font-semibold leading-tight tracking-tight text-primary-dark mb-0', {
  variants: {
    border: {
      true: 'border-b border-solid border-bright pb-2'
    }
  },
  defaultVariants: {
    border: false
  }
});

const DialogTitle = React.forwardRef(({ className, border, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(dialogTitleVariants({ border }), className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('mb-0 text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };
